import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "er-container wrapper m-y-8" }
const _hoisted_2 = { class: "flow" }
const _hoisted_3 = {
  key: 0,
  class: "m-y-7"
}
const _hoisted_4 = { class: "flow m-t-7" }
const _hoisted_5 = { key: 1 }

import { ref, onBeforeMount, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useFetch } from '@/hooks';
import { DropdownValue, FieldType, FilterValuesResponse, HttpError, SearchResult } from '@/types';
import debounce from 'debounce';
import { useI18n } from 'vue-i18n';
import configHelper from '../helpers/configHelper';
import SearchForm from '../components/organisms/SearchForm.vue';
import constants from '../config/constants';
import {
  dropdownFilterKeys,
  filterKeys,
  isNumericFilter,
  numericFilterKeys,
  numericFilters,
} from '../config/searchFilters';
import DropdownProduct from '../components/organisms/DropdownProduct.vue';
import Spinner from '../components/atoms/Spinner.vue';
import languageHelper from '../helpers/languageHelper';
import Pagination from '../components/organisms/Pagination.vue';
import { useNotifications } from '../hooks/useNotifications';

interface SearchPages extends Omit<SearchResult, 'data'> {
  pages: SearchResult['data'][];
}

/** The backend only holds german and english properties, everything else falls back to english */

export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const i18n = useI18n();
const router = useRouter();
const route = useRoute();
const { fetchApi, isLoading } = useFetch(configHelper.PDA_API);
const { fetchApi: fetchPage } = useFetch();
const { showNotification } = useNotifications();

const apiLang = ['de', 'en'].includes(languageHelper.getLanguage())
  ? languageHelper.getLanguage()
  : 'en';

/**
 * Filters a user picked before, taken from the url once. They only seed the form, so reading them
 * reactively would rebuild every field whenever the url is updated with the current search.
 */
const initialQuery = { ...route.query };

const initalSearch = ref(route.query.search?.toString() || '');
const searchResult = ref<SearchPages>();
const possibleYears = ref(Array<DropdownValue>());
const filterValues = ref<FilterValuesResponse['dropdown']>();
/** (Sub)year the offered filter values belong to, an empty string for all years */
const filterValuesYear = ref('');
const searchForm = ref<InstanceType<typeof SearchForm>>();
const formReady = ref(false);
const currentParams = ref<URLSearchParams>();
const currentPage = ref(0);
const noSearchResult = ref(false);

/**
 * * FUNCTIONS
 * */

/**
 * Translation key of a filter label, i.e. `filterHousingColor`. The keys are prefixed, as the
 * backend translations are merged into the same messages and would overwrite plainer keys.
 * @param {string} key - query param of the filter
 */
const filterLabel = (key: string) => `filter${key.charAt(0).toUpperCase()}${key.slice(1)}`;

/**
 * Handle a search submit and execute the actual search
 * @param inputs - Search inputs where keys match backend requirements and will be queried
 */
const handleSubmit = async (inputs: { [key: string]: string }) => {
  searchResult.value = undefined;
  try {
    // create the search params from input values
    const params = new URLSearchParams();
    const invalidFilters: string[] = [];

    Object.keys(inputs).forEach((key) => {
      if (!inputs[key]) return;

      // The backend rejects a number outside of its bounds with a 400
      if (isNumericFilter(key) && !numericFilters[key].isValid(inputs[key])) {
        invalidFilters.push(key);
        return;
      }

      params.append(key, inputs[key]);
    });

    // Point the entered value out instead of silently searching without it, which would show
    // results that do not match what a user asked for
    if (invalidFilters.length) {
      invalidFilters.forEach((key) =>
        showNotification(
          i18n.t('invalidFilterValue', {
            filter: i18n.t(filterLabel(key)),
            hint: i18n.t(`${filterLabel(key)}Placeholder`),
          }),
          'warning',
        ),
      );
      return;
    }

    // A search needs a term or at least one property filter, so we never match the whole archive.
    // Year, type, download type, language and region do not narrow it down enough on their own.
    const searchTerm = inputs.search || '';
    const hasFilter = filterKeys.some((key) => params.has(key));

    // Only search if a user enters at least 3 characters or picked a property filter
    if ((searchTerm && searchTerm.length < 3) || (!searchTerm && !hasFilter)) {
      showNotification(i18n.t('minCharacterLengthSearch'), 'error');
      return;
    }

    // The dropdown values are language dependent, so they have to be sent back in the language
    // they were requested in
    params.append('lang', apiLang);

    // Query the backend
    const response = await fetchApi<SearchResult>(`/search?${params.toString()}`, {
      useCredentials: true,
    });

    // Fill refs with ne values
    searchResult.value = {
      next: response.next,
      limit: response.limit,
      offset: response.offset,
      pages: [response.data],
    };
    currentPage.value = 0;

    // prepare and set params for pagination
    params.delete('search');
    currentParams.value = params;
    noSearchResult.value = false;
  } catch (error) {
    if (error instanceof HttpError && error.code === 404) {
      noSearchResult.value = true;
    } else {
      console.log(error);
      showNotification(i18n.t('genericError') + i18n.t('searchLoadError'), 'error');
    }

    searchResult.value = undefined;
    currentParams.value = undefined;
  }
};

/**
 * Load the values every dropdown filter offers
 * @param {string} year - (sub)year to limit the values to, empty to use all years
 */
const loadFilterValues = async (year?: string) => {
  const params = new URLSearchParams({ lang: apiLang });

  if (year) params.append('year', year);

  const response = await fetchApi<FilterValuesResponse>(`/configs/filters?${params.toString()}`, {
    useCredentials: true,
  });

  return response.dropdown;
};

/**
 * Offer the filter values of the picked year. A year which does not hold a value a user already
 * picked is undone, as that filter would silently stop matching anything.
 * @param inputs - current inputs of the form
 * @returns whether the year could be applied and the search may run
 */
const applyYearScope = async (inputs: { [key: string]: string }): Promise<boolean> => {
  const year = inputs.year || '';

  // Without any filter values there are no dropdowns to scope, i.e. the endpoint is unavailable
  if (!filterValues.value || year === filterValuesYear.value) return true;

  let dropdown: FilterValuesResponse['dropdown'];

  try {
    dropdown = await loadFilterValues(year);
  } catch (error) {
    showNotification(i18n.t('genericError') + i18n.t('filterLoadError'), 'error');
    console.log(error);
    // The picked filters could not be verified, so the year they were picked for is kept
    searchForm.value?.setInput('year', filterValuesYear.value);
    return false;
  }

  const unavailable = dropdownFilterKeys.filter(
    (key) => inputs[key] && !dropdown[key]?.includes(inputs[key]),
  );

  if (unavailable.length) {
    unavailable.forEach((key) =>
      showNotification(
        i18n.t('filterUnavailableInYear', {
          filter: i18n.t(filterLabel(key)),
          value: inputs[key],
          year,
        }),
        'error',
      ),
    );

    searchForm.value?.setInput('year', filterValuesYear.value);
    return false;
  }

  filterValues.value = dropdown;
  filterValuesYear.value = year;

  return true;
};

/** Run a search a user explicitly asked for, once the picked year could be applied */
const onFormSubmit = async (inputs: { [key: string]: string }) => {
  if (await applyYearScope(inputs)) handleSubmit(inputs);
};

const onFormChange = debounce(async (inputs: { [key: string]: string }) => {
  // Undoing a year emits another change, which then runs the search
  if (!(await applyYearScope(inputs))) return;

  noSearchResult.value = false;
  const clearInputs = Object.entries(inputs).filter(([_key, value]) => value);
  router.replace({ path: '/search', query: { ...Object.fromEntries(clearInputs) } });

  if (clearInputs.length) handleSubmit(inputs);
  else searchResult.value = undefined;
}, 500);

/**
 * * HOOKS
 * */

// Handle pagination, always tries to query a extra page, so it will be instantly available for the user
watch([currentPage, searchResult], async ([newPage, newSearchResult], [oldPage]) => {
  if (
    newPage !== oldPage &&
    newSearchResult &&
    newSearchResult.next !== null &&
    newPage + 1 >= newSearchResult.pages.length
  ) {
    try {
      const response = await fetchPage<SearchResult>(newSearchResult.next, {
        useCredentials: true,
      });

      searchResult.value = {
        next: response.next,
        limit: response.limit,
        offset: response.offset,
        pages: [...newSearchResult.pages, response.data],
      };
    } catch (error) {
      showNotification(i18n.t('genericError') + i18n.t('pageLoadError'), 'error');
      console.log(error);
    }
  }
});

/** Load the years the archive holds a collection for, they are offered as a dropdown */
const loadYears = async () => {
  try {
    const years = await fetchApi<Array<string>>('/configs/years', { useCredentials: true });
    years.sort();

    years.forEach((year) => {
      possibleYears.value.push({ id: year, title: year });
    });
  } catch (error) {
    showNotification(i18n.t('genericError') + i18n.t('yearLoadError'), 'error');
    console.log(error);
  }
};

/** Offer the filters of the year the form starts with, without them their dropdowns stay hidden */
const loadInitialFilterValues = async () => {
  try {
    filterValues.value = await loadFilterValues();
  } catch (error) {
    showNotification(i18n.t('genericError') + i18n.t('filterLoadError'), 'error');
    console.log(error);
  }
};

onBeforeMount(async () => {
  // The form can start on a year, which the offered filter values are scoped to right away
  filterValuesYear.value = initialQuery.year?.toString() || '';

  // Neither request rejects, a failed one just leaves its dropdown(s) out of the form. The form is
  // rendered afterwards, as it takes the values a user picked before from the url of its fields,
  // which are only complete once the values they offer are known.
  await Promise.all([loadYears(), loadInitialFilterValues()]);

  formReady.value = true;
});

/**
 * * Transformed values
 * */

const downloadTypes = computed(() => {
  const output: Array<DropdownValue> = [];

  Object.keys(constants.downloadTypes).forEach((key) => {
    const element = constants.downloadTypes[key as unknown as keyof typeof constants.downloadTypes]; // wtf is this
    output.push({
      id: key,
      title: i18n.t(element.platzhalter),
    });
  });

  output.sort((a, b) => {
    if (a.title > b.title) return 1;
    if (a.title < b.title) return -1;
    return 0;
  });

  return output;
});

const languages: Array<DropdownValue> = [];

constants.langs.forEach((lang) => {
  if (lang.id)
    languages.push({
      id: lang.id.toString(),
      title: `lang_${lang.id}`,
    });
});

/**
 * The property filters, offered in a collapsed section below the always visible fields. The
 * dropdowns are left out if their values could not be loaded, the numeric fields do not depend
 * on them and are always available.
 */
const advancedFields = computed(() => {
  const fields: Array<FieldType> = [];

  dropdownFilterKeys.forEach((key) => {
    const values = filterValues.value?.[key];

    if (!values?.length) return;

    const queryValue = initialQuery[key]?.toString();

    fields.push({
      type: 'dropdown',
      key,
      title: i18n.t(filterLabel(key)),
      values: values.map((value) => ({ id: value, title: value })),
      allowAll: true,
      // The values are language dependent, so a link from another language holds ones which are
      // not selectable here. The dropdown could not show them, but they would still be applied.
      initialValue: queryValue && values.includes(queryValue) ? queryValue : undefined,
    });
  });

  numericFilterKeys.forEach((key) => {
    const { unit, inputPattern, inputMode } = numericFilters[key];
    const queryValue = initialQuery[key]?.toString();

    fields.push({
      type: 'input',
      key,
      title: i18n.t(filterLabel(key)),
      placeholder: i18n.t(`${filterLabel(key)}Placeholder`),
      unit,
      inputPattern,
      inputMode,
      // A url can hold anything, but the field must never start with a value it would not accept
      initialValue: queryValue && inputPattern.test(queryValue) ? queryValue : undefined,
    });
  });

  return fields;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _createElementVNode("h1", null, _toDisplayString(_ctx.$t('title')), 1),
      (!formReady.value)
        ? (_openBlock(), _createBlock(Spinner, {
            key: 0,
            center: true
          }))
        : (_openBlock(), _createBlock(SearchForm, {
            key: 1,
            ref_key: "searchForm",
            ref: searchForm,
            fields: [
          {
            type: 'dropdown',
            key: 'type',
            title: _ctx.$t('productType'),
            values: [
              { id: 'article', title: 'article' },
              { id: 'accessory', title: 'accessory' },
            ],
            allowAll: true,
            translateTitle: true,
            initialValue: _unref(route).query.type?.toString(),
          },
          {
            type: 'input',
            key: 'search',
            title: _ctx.$t('search'),
            initialValue: initalSearch.value,
            placeholder: _ctx.$t('search...'),
          },
          {
            type: 'dropdown',
            key: 'downloadTyp',
            title: _ctx.$t('downloadTyp'),
            values: _unref(downloadTypes),
            translateTitle: false,
            allowAll: true,
            initialValue: _unref(route).query.downloadTyp?.toString(),
          },
          {
            type: 'dropdown',
            key: 'language',
            title: _ctx.$t('language'),
            values: languages,
            translateTitle: true,
            allowAll: true,
            initialValue: _unref(route).query.language?.toString(),
          },
          {
            type: 'dropdown',
            key: 'region',
            title: _ctx.$t('technicalRegion'),
            values: _unref(constants).technicalRegions,
            allowAll: true,
            initialValue: _unref(route).query.region?.toString(),
          },
          {
            type: 'dropdown',
            key: 'year',
            title: _ctx.$t('year'),
            values: possibleYears.value,
            allowAll: true,
            initialValue: _unref(route).query.year?.toString(),
            hint: _ctx.$t('yearHint'),
          },
        ],
            "advanced-fields": _unref(advancedFields),
            "advanced-title": _ctx.$t('advancedFilters'),
            onSubmit: onFormSubmit,
            onChange: _unref(onFormChange)
          }, null, 8, ["fields", "advanced-fields", "advanced-title", "onChange"]))
    ]),
    (_unref(isLoading) && formReady.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(Spinner, { center: true })
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("section", _hoisted_4, [
      (searchResult.value?.pages.length)
        ? (_openBlock(), _createBlock(Pagination, {
            key: 0,
            items: searchResult.value.pages.flat(),
            "page-size": searchResult.value.limit,
            "has-next-page": searchResult.value.pages.length > currentPage.value,
            "onUpdate:page": _cache[0] || (_cache[0] = (value) => (currentPage.value = value))
          }, {
            default: _withCtx((slotprops) => [
              (_openBlock(), _createBlock(DropdownProduct, {
                key: slotprops.key,
                product: slotprops.item
              }, null, 8, ["product"]))
            ]),
            _: 1
          }, 8, ["items", "page-size", "has-next-page"]))
        : _createCommentVNode("", true)
    ]),
    (noSearchResult.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_5, [
          _createElementVNode("h3", null, _toDisplayString(_ctx.$t('noSearchResult')), 1)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})